import { BUTTON_SIZE, BUTTON_TYPE, DSButton } from '@repo/design-system/index'
import { useState } from 'react'
import Rating from 'react-rating'
import { HACKLE_FLAG_KEY, useFeatureFlag } from '../../../../helpers/hackleHelper'
import apis from '../../../../modules/apis'
import { axiosGet } from '../../../../modules/axiosHelper'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import { n_common, n_review } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import {
  LESSON_EVALUATION_ALARM_BLACK,
  LESSON_EVALUATION_STAR_GRAY,
  LESSON_EVALUATION_STAR_YELLOW,
} from '../../../../modules/images'
import { ICON_CHEVRON_RIGHT_BLACK } from '../../../../modules/svg/svg'
import urls from '../../../../modules/urls'
import useEvaluationStore from '../../../../store/useEvaluationStore'
import { useBasicPopup } from '../../../basic/popup/BasicPopup'
import LessonEvaluationReport from '../../../student/new-portal/lessons/LessonEvaluationReport'
import { Tutor } from './EvaluationModalBody'

type RatingBoxPropsType = {
  rate: number
  setRate: (rate: number) => void
  setHover: (rate: number) => void
}

const RatingBox = ({ rate, setRate, setHover }: RatingBoxPropsType) => {
  const onHoverRate = (rate?: number) => {
    setHover(rate ?? 0)
  }

  return (
    <div className="flex h-[25px] select-none flex-row">
      <Rating
        initialRating={rate}
        onHover={onHoverRate} // When hover ends, gets called with no value (i.e. undefined as the value).
        onChange={setRate}
        emptySymbol={<img alt="star_gray" src={LESSON_EVALUATION_STAR_GRAY} className="mr-[6px] h-[25px] w-[25px]" />}
        fullSymbol={
          <img alt="star_yellow" src={LESSON_EVALUATION_STAR_YELLOW} className="mr-[6px] h-[25px] w-[25px]" />
        }
        stop={5}
      />
    </div>
  )
}

interface ILessonEvaluationStep1 {
  next: () => void
  lessonId: number | string
  tutorData: Tutor
  refetch: () => void
  sendClickEventToGTM: (buttonName: string) => void
  isRookie: boolean
  onClose: () => void
}
const EvaluationStep1 = ({
  next,
  lessonId,
  tutorData,
  refetch,
  sendClickEventToGTM,
  isRookie,
  onClose,
}: ILessonEvaluationStep1) => {
  const { t } = useTranslation()
  const basicPopup = useBasicPopup()

  const router = useAppRouter()
  const [hoverRate, setHoverRate] = useState(0)

  const evaluationRate = useEvaluationStore((state) => state.evaluationRate)
  const setEvaluationRate = useEvaluationStore((state) => state.setEvaluationRate)

  const isClassroom = router?.asPath?.includes('/lessons/classroom/') ?? false
  const moveLessonCompletePage = useFeatureFlag(HACKLE_FLAG_KEY.MOVE_LESSON_COMPLETE_PAGE)

  const handleReportPopup = async (lessonId: number) => {
    onClose()
    const response = await axiosGet(apis.lesson.get(lessonId))

    if (response) {
      basicPopup.show({
        id: 'lessons-report-popup',
        maxW: 600,
        bodyPb: 32,
        body: (
          <LessonEvaluationReport
            lessonId={lessonId}
            lessonData={response.lesson}
            refetch={() => refetch()}
            onClose={() => basicPopup.close('lessons-report-popup')}
          />
        ),
      })
    }
  }

  const getLessonEvalDesc = (rate: number, hoverRate: number) => {
    const targetRate = hoverRate > 0 ? hoverRate : rate

    const ratingTexts = [
      t(n_review.step_1.rate_placeholder),
      t(n_review.step_1.rate_1),
      t(n_review.step_1.rate_2),
      t(n_review.step_1.rate_3),
      t(n_review.step_1.rate_4),
      t(n_review.step_1.rate_5),
    ]

    return ratingTexts[targetRate] ?? ratingTexts[0]
  }

  const isAvailableNext = evaluationRate && evaluationRate > 0

  const handleNextButton = (isAvailableNext: boolean) => {
    if (isAvailableNext) {
      next()
      sendClickEventToGTM('다음')
    }
  }

  const handleLaterButton = () => {
    onClose()
    sendClickEventToGTM('나중에 하기')
    if (isClassroom) {
      if (moveLessonCompletePage) {
        router.push(urls.student.newPortal.lessons.complete + '?lesson_id=' + lessonId)
      } else {
        router.push(urls.student.newPortal.lessons.reviews.home)
      }
    }
  }

  return (
    <div className="flex h-[calc(100vh_-_100px)] flex-col justify-between sm:h-full">
      <p className="Headline2-24Bd  font-medium">{t(n_review.step_1.title(tutorData.name))}</p>

      <div className="flex h-full items-center justify-center sm:h-[332px]">
        <div className="flex flex-col items-center justify-center">
          <img src={tutorData.image_url} alt="tutor_profile" className="h-[80px] w-[80px] rounded-full object-cover" />
          <div className="mt-[8px] mb-[11px] font-bold">{tutorData.name}</div>
          <div>
            <RatingBox
              rate={evaluationRate}
              setHover={(rate) => setHoverRate(rate)}
              setRate={(rate) => setEvaluationRate(rate)}
            />
          </div>
          <div className="mt-[20px] text-[14px] font-medium">{getLessonEvalDesc(evaluationRate, hoverRate)}</div>
        </div>
      </div>
      <div
        className="hidden cursor-pointer flex-row justify-between bg-gray-50 py-[12px] px-[16px] sm:flex"
        onClick={() => handleReportPopup(+lessonId)}
      >
        <div className="flex flex-row gap-[8px] text-[12px]">
          <img src={LESSON_EVALUATION_ALARM_BLACK} alt="alarm" className="h-[20px] w-[20px]" />
          <div>{t(n_review.step_1.report)}</div>
        </div>
        <img alt="right" src={ICON_CHEVRON_RIGHT_BLACK} />
      </div>

      <div className="flex justify-end gap-[8px] sm:mt-[40px] ">
        <div className="hidden sm:block sm:h-[46px] sm:w-[112px]">
          <DSButton size={BUTTON_SIZE.MD} type={BUTTON_TYPE.OUTLINE} onClick={() => handleLaterButton()}>
            <DSButton.Label>{t(n_common.btn_leave)}</DSButton.Label>
          </DSButton>
        </div>
        <div className="mb-[34px] w-full sm:mb-[0px] sm:h-[46px] sm:w-[112px]">
          <DSButton
            size={BUTTON_SIZE.MD}
            type={BUTTON_TYPE.SOLID}
            disabled={!isAvailableNext}
            onClick={() => handleNextButton(isAvailableNext)}
            className="border-1 border-transparent"
          >
            <DSButton.Label>{t(n_review.step_1.btn_2)}</DSButton.Label>
          </DSButton>
        </div>
      </div>
    </div>
  )
}

export default EvaluationStep1
